import { useState, useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Package2,
  Users,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  RefreshCw,
  Calendar,
  Star,
  Box,
  Truck,
  CreditCard,
  Activity,
  BarChart3,
  ShoppingBag,
  UserPlus,
  PackageX,
  Timer,
  FileText,
  Download,
  PieChart,
  Wallet,
  Receipt,
  FileSpreadsheet,
  ArrowUpRight,
  ArrowDownRight,
  Layers,
} from "lucide-react";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import Price from "../../components/Currency/Price";
import { Tooltip, useMediaQuery, useTheme } from "@mui/material";
import {
  DashboardStats,
  FinancialDashboard,
  Order,
  SalesData,
  TopProduct,
  LowStockProduct,
  RecentActivity,
  RevenueData,
  ExpensesData,
  ProfitLossData,
  BalanceSheet,
  CashFlowData,
  InventoryReport,
  Report,
} from "../../types/Dashboard";
import LoadingComponent from "../../components/LoadingComponent";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { formatDateForReports } from "../../utils/dateFormatter";
import axios from "axios";
import { useCurrency } from "../../components/Currency/CurrencyContext";

const getDateInputValue = (value?: Date | string) => {
  if (!value) return "";

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmedValue)) {
      return trimmedValue.slice(0, 10);
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().split("T")[0];
};

const createGenerateReportData = (
  defaultStartDate?: string,
  defaultEndDate?: string,
  reportType = "",
) => {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  return {
    reportType,
    startDate:
      getDateInputValue(defaultStartDate) || getDateInputValue(thirtyDaysAgo),
    endDate: getDateInputValue(defaultEndDate) || getDateInputValue(today),
    format: "excel",
  };
};

const extractFilename = (
  contentDisposition: string | undefined,
  reportType: string,
  format: string,
) => {
  if (contentDisposition) {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (simpleMatch?.[1]) {
      return simpleMatch[1];
    }
  }

  const extension = format === "pdf" ? "pdf" : "xlsx";
  return `${reportType}-report.${extension}`;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DashboardPage = () => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const { permissions, token } = useAuth();
  // console.log("User Permissions:", permissions);
  // Check if user has permission to create or edit products
  const canViewReports =
    hasPermission(permissions, PERMISSIONS.VIEW_REPORTS) ||
    permissions[0] === "*";
  const canExport =
    hasPermission(permissions, PERMISSIONS.EXPORT_REPORTS) ||
    permissions[0] === "*";
  // User can add category if they have either create or edit permission
  const canViewExport = canViewReports || canExport;

  // console.log("canViewExport:", canViewExport);

  // State
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [financialDashboard, setFinancialDashboard] =
    useState<FinancialDashboard | null>(null);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [salesChartData, setSalesChartData] = useState<SalesData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [lowStockProducts, setLowStockProducts] = useState<LowStockProduct[]>(
    [],
  );
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<
    "week" | "month" | "year"
  >("week");

  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [expensesData, setExpensesData] = useState<ExpensesData | null>(null);
  const [profitLossData, setProfitLossData] = useState<ProfitLossData | null>(
    null,
  );
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheet | null>(null);
  const [cashFlowData, setCashFlowData] = useState<CashFlowData | null>(null);
  const [inventoryReport, setInventoryReport] =
    useState<InventoryReport | null>(null);

  const [reports, setReports] = useState<Report[]>([]);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [generateReportModal, setGenerateReportModal] = useState(false);
  const [generateReportData, setGenerateReportData] = useState(() =>
    createGenerateReportData(),
  );
  const [generatingReportType, setGeneratingReportType] = useState<
    string | null
  >(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Update date range when period changes
  useEffect(() => {
    const { startDate: start, endDate: end } = getDateRange(selectedPeriod);
    setStartDate(start);
    setEndDate(end);
  }, [selectedPeriod]);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod, startDate, endDate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Format dates for API
      const format = "json"; // or 'pdf', 'excel'
      const groupBy =
        selectedPeriod === "week"
          ? "day"
          : selectedPeriod === "month"
            ? "week"
            : "month";

      // Parallel API calls for better performance
      const [
        financialRes,
        revenueRes,
        expensesRes,
        profitLossRes,
        balanceSheetRes,
        cashFlowRes,
        lowStockRes,
        salesReportRes,
        inventoryReportRes,
        financialReportRes,
        deliveryReportRes,
        giftsReportRes,
      ] = await Promise.allSettled([
        BaseApi.get("/financial/dashboard"),
        BaseApi.get("/financial/revenue"),
        BaseApi.get("/financial/expenses"),
        BaseApi.get("/financial/profit-loss"),
        BaseApi.get("/financial/balance-sheet"),
        BaseApi.get("/financial/cash-flow"),
        BaseApi.get("/inventory/low-stock"),
        BaseApi.get(
          `/reports/sales?startDate=${startDate}&endDate=${endDate}&format=${format}&groupBy=${groupBy}`,
        ),
        BaseApi.get("/reports/inventory"),
        BaseApi.get(
          `/reports/financial?startDate=${startDate}&endDate=${endDate}&format=${format}&groupBy=${groupBy}`,
        ),
        BaseApi.get(
          `/reports/delivery?startDate=${startDate}&endDate=${endDate}&format=${format}&groupBy=${groupBy}`,
        ),
        BaseApi.get(
          `/reports/gifts?startDate=${startDate}&endDate=${endDate}&format=${format}&groupBy=${groupBy}`,
        ),
      ]);

      if (financialRes.status === "fulfilled" && financialRes.value?.success) {
        setFinancialDashboard(financialRes.value.data);
      }

      if (revenueRes.status === "fulfilled" && revenueRes.value?.success) {
        setRevenueData(revenueRes.value.data);
      }

      if (expensesRes.status === "fulfilled" && expensesRes.value?.success) {
        setExpensesData(expensesRes.value.data);
      }

      if (
        profitLossRes.status === "fulfilled" &&
        profitLossRes.value?.success
      ) {
        setProfitLossData(profitLossRes.value.data);
      }

      if (
        balanceSheetRes.status === "fulfilled" &&
        balanceSheetRes.value?.success
      ) {
        setBalanceSheet(balanceSheetRes.value.data);
      }

      if (cashFlowRes.status === "fulfilled" && cashFlowRes.value?.success) {
        setCashFlowData(cashFlowRes.value.data);
      }

      if (lowStockRes.status === "fulfilled" && lowStockRes.value?.success) {
        setLowStockProducts(lowStockRes.value.data);
      }

      if (
        inventoryReportRes.status === "fulfilled" &&
        inventoryReportRes.value?.success
      ) {
        setInventoryReport(inventoryReportRes.value.data);
      }

      // Collect reports
      const reportsData: Report[] = [];
      if (salesReportRes.status === "fulfilled" && salesReportRes.value?.data) {
        reportsData.push({
          id: "sales",
          name: "Sales Report",
          type: "sales",
          generatedAt: formatDateForReports(new Date()),
        });
      }
      if (
        inventoryReportRes.status === "fulfilled" &&
        inventoryReportRes.value?.data
      ) {
        reportsData.push({
          id: "inventory",
          name: "Inventory Report",
          type: "inventory",
          generatedAt: formatDateForReports(new Date()),
        });
      }
      if (
        financialReportRes.status === "fulfilled" &&
        financialReportRes.value?.data
      ) {
        reportsData.push({
          id: "financial",
          name: "Financial Report",
          type: "financial",
          generatedAt: formatDateForReports(new Date()),
        });
      }
      if (
        deliveryReportRes.status === "fulfilled" &&
        deliveryReportRes.value?.data
      ) {
        reportsData.push({
          id: "delivery",
          name: "Delivery Report",
          type: "delivery",
          generatedAt: formatDateForReports(new Date()),
        });
      }
      if (giftsReportRes.status === "fulfilled" && giftsReportRes.value?.data) {
        reportsData.push({
          id: "gifts",
          name: "Gift Orders Report",
          type: "gifts",
          generatedAt: formatDateForReports(new Date()),
        });
      }
      setReports(reportsData);
    } catch (error: any) {
      showToast(error.message || "Failed to load dashboard data", "err");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  const openGenerateReportModal = (reportType = "") => {
    setGenerateReportData(
      createGenerateReportData(startDate, endDate, reportType),
    );
    setGenerateReportModal(true);
  };

  const closeGenerateReportModal = () => {
    setGenerateReportModal(false);
    setGenerateReportData(createGenerateReportData(startDate, endDate));
  };

  async function downloadReportAxios(
    type: string,
    reportStartDate: string,
    reportEndDate: string,
    format = "excel",
  ) {
    if (!token) {
      throw new Error("Authentication token not found");
    }

    setGeneratingReportType(type);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reports/send`,
        {
          type,
          startDate: formatDateForReports(
            new Date(`${reportStartDate}T00:00:00`),
          ),
          endDate: formatDateForReports(new Date(`${reportEndDate}T23:59:59`)),
          format,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      const disposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const fileName = extractFilename(disposition, type, format);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setGeneratingReportType(null);
    }
  }

  const submitGenerateReport = async () => {
    if (
      !generateReportData.reportType ||
      !generateReportData.startDate ||
      !generateReportData.endDate ||
      !generateReportData.format
    ) {
      showToast(
        "Please select the report type, format, start date, and end date.",
        "err",
      );
      return;
    }

    if (
      new Date(generateReportData.startDate) >
      new Date(generateReportData.endDate)
    ) {
      showToast("Start date cannot be later than end date.", "err");
      return;
    }

    try {
      await downloadReportAxios(
        generateReportData.reportType,
        generateReportData.startDate,
        generateReportData.endDate,
        generateReportData.format,
      );
      showToast("Report generated successfully.", "success");
      closeGenerateReportModal();
    } catch (error: any) {
      console.error("Error generating report:", error);
      showToast(error.message || "Failed to generate report.", "err");
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getOrderStatusConfig = (status: string) => {
    const configs = {
      completed: {
        color: "bg-green-50 text-green-700 border-green-200 shadow-green-100",
        iconColor: "text-green-600",
        icon: CheckCircle,
      },
      processing: {
        color: "bg-blue-100 text-blue-700 border-[#0d223f] shadow-blue-100",
        iconColor: "text-blue-600",
        icon: Truck,
      },
      pending: {
        color:
          "bg-yellow-50 text-yellow-700 border-yellow-200 shadow-yellow-100",
        iconColor: "text-yellow-600",
        icon: Clock,
      },
      cancelled: {
        color: "bg-red-50 text-red-700 border-red-200 shadow-red-100",
        iconColor: "text-red-600",
        icon: XCircle,
      },
    };
    return configs[status as keyof typeof configs] || configs.pending;
  };

  const getMaxSales = () => {
    if (salesChartData.length === 0) return 0;
    return Math.max(...salesChartData.map((d) => d.sales));
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("en-US").format(num);
  };

  const isGeneratingSelectedReport = generateReportData.reportType
    ? generatingReportType === generateReportData.reportType
    : false;

  // Get date range for week/month/year
  const getDateRange = (period: "week" | "month" | "year") => {
    const end = new Date();
    const start = new Date();

    switch (period) {
      case "week":
        // Start of current week (Sunday)
        const dayOfWeek = start.getDay();
        start.setDate(start.getDate() - dayOfWeek);
        start.setHours(0, 0, 0, 0);
        break;
      case "month":
        // Start of current month
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        break;
      case "year":
        // Start of current year
        start.setMonth(0, 1);
        start.setHours(0, 0, 0, 0);
        break;
    }

    // End of current day
    end.setHours(23, 59, 59, 999);

    return {
      startDate: formatDateForReports(start),
      endDate: formatDateForReports(end),
    };
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="min-h-screen bg-[#f2e6db] py-4 sm:py-8 px-4">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideRight {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideLeft {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          background-size: 1000px 100%;
          animation: shimmer 2s infinite;
        }
        .hover-scale {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .hover-scale:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }
        .stat-card {
          position: relative;
          overflow: hidden;
        }
        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s;
        }
        .stat-card:hover::before {
          left: 100%;
        }
      `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div className="mb-2" style={{ animation: "fadeIn 0.5s ease-out" }}>
          <div className="bg-white flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-2xl shadow-xl p-4 sm:p-6 mb-1 sm:mb-2">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {t("Dashboard")}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  <p className="text-gray-600 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(startDate).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}{" "}
                    -{" "}
                    {new Date(endDate).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3 sm:w-auto">
                <div className="border flex rounded-lg shadow-md p-1">
                  {(["week", "month", "year"] as const).map((period) => (
                    <button
                      key={period}
                      onClick={() => setSelectedPeriod(period)}
                      className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                        selectedPeriod === period
                          ? "bg-indigo-600 text-white shadow-lg"
                          : "text-white-600 hover:text-black"
                      }`}
                    >
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex border items-center gap-2 px-6 py-3 bg-white text-gray-700 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}
                />
                {!isMobile && (refreshing ? t("Refreshing...") : t("Refresh"))}
              </button>
            </div>
          </div>
        </div>

        {loading ? <LoadingComponent title={t("Dashboard")} /> : null}
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Revenue Card */}
          {stats?.revenue && (
            <div
              className="stat-card bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover-scale"
              style={{ animation: "slideRight 0.5s ease-out" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-lg">
                  {currency.symbol}
                </div>
                <div
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold ${
                    stats.revenue.trend === "up"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {stats.revenue.trend === "up" ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {stats.revenue.change}%
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">
                {t("Total Revenue")}
              </h3>
              <p className="text-3xl font-bold text-gray-500 mb-2">
                <Price amount={Number(stats.revenue.total)} />
              </p>
              <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                <span>
                  {t("Today")}: <Price amount={Number(stats.revenue.today)} />
                </span>
                <span>
                  {t("Month")}:{" "}
                  <Price amount={Number(stats.revenue.thisMonth)} />
                </span>
              </div>
            </div>
          )}

          {/* Orders Card */}
          {stats?.orders && (
            <div
              className="stat-card bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover-scale"
              style={{ animation: "slideRight 0.5s ease-out 0.1s backwards" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl shadow-lg">
                  <ShoppingCart className="w-6 h-6 text-white" />
                </div>
                <div
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold ${
                    stats.orders.trend === "up"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {stats.orders.trend === "up" ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {stats.orders.change}%
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">
                {t("Total Orders")}
              </h3>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                {formatNumber(stats.orders.total || 0)}
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs pt-3 border-t border-gray-100">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <span className="text-gray-600">
                    {t("Pending")}: {stats.orders.pending}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#0d223f]"></div>
                  <span className="text-gray-600">
                    {t("Processing")}: {stats.orders.processing}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Products Card */}
          {stats?.products && (
            <div
              className="stat-card bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover-scale"
              style={{ animation: "slideRight 0.5s ease-out 0.2s backwards" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-xl shadow-lg">
                  <Package2 className="w-6 h-6 text-white" />
                </div>
                {stats.products.lowStock > 0 && (
                  <div className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold bg-red-100 text-red-700">
                    <AlertTriangle className="w-4 h-4" />
                    {stats.products.lowStock}
                  </div>
                )}
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">
                {t("Total Products")}
              </h3>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                {formatNumber(stats.products.total || 0)}
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs pt-3 border-t border-gray-100">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-gray-600">
                    {t("Active")}: {stats.products.active}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-gray-600">
                    {t("Out of Stock")}: {stats.products.outOfStock}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Customers Card */}
          {stats?.customers && (
            <div
              className="stat-card bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover-scale"
              style={{ animation: "slideRight 0.5s ease-out 0.3s backwards" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl shadow-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold ${
                    stats.customers.trend === "up"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {stats.customers.trend === "up" ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {stats.customers.change}%
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">
                {t("Total Customers")}
              </h3>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                {formatNumber(stats.customers.total || 0)}
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs pt-3 border-t border-gray-100">
                <div className="flex items-center gap-1">
                  <UserPlus className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-600">
                    {t("New")}: {stats.customers.new}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-600">
                    {t("Active")}: {stats.customers.active}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Financial Overview with Real Data */}
        {(profitLossData ||
          cashFlowData ||
          balanceSheet ||
          financialDashboard) && (
          <div
            className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 mb-8"
            style={{ animation: "fadeIn 0.5s ease-out 0.5s backwards" }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Wallet className="w-6 h-6 text-indigo-600" />
                  {t("Financial Overview")}
                </h2>
              </div>
            </div>

            {/* Primary Financial Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              {/* Revenue */}
              {profitLossData?.revenue && (
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-400">
                  <div className="flex items-center gap-2 mb-2">
                    {currency.symbol}
                    <h3 className="text-sm font-semibold text-blue-900">
                      {t("Total Revenue")}
                    </h3>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    <Price amount={Number(profitLossData.revenue.total)} />
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {t("Sales")}:{" "}
                    <Price amount={Number(profitLossData.revenue.sales)} />
                  </p>
                </div>
              )}

              {/* Expenses */}
              {expensesData && (
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-5 border border-red-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="w-5 h-5 text-red-600" />
                    <h3 className="text-sm font-semibold text-red-900">
                      {t("Total Expenses")}
                    </h3>
                  </div>
                  <p className="text-2xl font-bold text-red-900">
                    <Price amount={Number(expensesData.totalExpenses)} />
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    {expensesData.expenseCount} {t("transactions")}
                  </p>
                </div>
              )}

              {/* Net Profit */}
              {profitLossData?.netIncome && (
                <div
                  className={`bg-gradient-to-br ${
                    profitLossData.netIncome.status === "profit"
                      ? "from-green-50 to-green-100 border-green-200"
                      : "from-red-50 to-red-100 border-red-200"
                  } rounded-xl p-5 border`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {profitLossData.netIncome.status === "profit" ? (
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    )}
                    <h3
                      className={`text-sm font-semibold ${
                        profitLossData.netIncome.status === "profit"
                          ? "text-green-900"
                          : "text-red-900"
                      }`}
                    >
                      {t("Net Income")}
                    </h3>
                  </div>
                  <p
                    className={`text-2xl font-bold ${
                      profitLossData.netIncome.status === "profit"
                        ? "text-green-900"
                        : "text-red-900"
                    }`}
                  >
                    <Price amount={Number(profitLossData.netIncome.amount)} />
                  </p>
                  <p
                    className={`text-xs mt-1 ${
                      profitLossData.netIncome.status === "profit"
                        ? "text-green-700"
                        : "text-red-700"
                    }`}
                  >
                    {Number(profitLossData.netIncome?.margin).toFixed(1)}%{" "}
                    {t("margin")}
                  </p>
                </div>
              )}

              {/* Profit Margin */}
              {profitLossData?.grossProfit && (
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <PieChart className="w-5 h-5 text-purple-600" />
                    <h3 className="text-sm font-semibold text-purple-900">
                      {t("Gross Profit")}
                    </h3>
                  </div>
                  <p className="text-2xl font-bold text-purple-900">
                    {Number(profitLossData.netIncome?.margin).toFixed(1)}%
                  </p>
                  <p className="text-xs text-purple-700 mt-1">
                    <Price amount={Number(profitLossData.grossProfit.amount)} />
                  </p>
                </div>
              )}

              {/* Cash Flow */}
              {cashFlowData && (
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-5 border border-teal-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-5 h-5 text-teal-600" />
                    <h3 className="text-sm font-semibold text-teal-900">
                      {t("Net Cash Flow")}
                    </h3>
                  </div>
                  <p className="text-2xl font-bold text-teal-900">
                    <Price amount={Number(cashFlowData.netCashChange)} />
                  </p>
                  <p className="text-xs text-teal-700 mt-1">
                    {t("Operating")}:{" "}
                    <Price
                      amount={Number(cashFlowData.operatingActivities.netCash)}
                    />
                  </p>
                </div>
              )}
            </div>

            {/* Balance Sheet Summary */}
            {balanceSheet && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-600">
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-indigo-900">
                      {t("Total Assets")}
                    </h4>
                    <Layers className="w-4 h-4 text-indigo-600" />
                  </div>
                  <p className="text-xl font-bold text-indigo-900 mb-2">
                    <Price amount={Number(balanceSheet.assets.total)} />
                  </p>
                  <div className="space-y-1 text-xs text-indigo-700">
                    <div className="flex justify-between">
                      <span>{t("Cash")}:</span>
                      <span>
                        <Price
                          amount={Number(
                            balanceSheet.assets?.currentAssets?.cash || 0,
                          )}
                        />
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("Receivables")}:</span>
                      <span>
                        <Price
                          amount={Number(
                            balanceSheet.assets?.currentAssets
                              ?.accountsReceivable || 0,
                          )}
                        />
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("Inventory")}:</span>
                      <span>
                        <Price
                          amount={Number(
                            balanceSheet.assets?.currentAssets?.inventory || 0,
                          )}
                        />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-amber-900">
                      {t("Total Liabilities")}
                    </h4>
                    <Receipt className="w-4 h-4 text-amber-600" />
                  </div>
                  <p className="text-xl font-bold text-amber-900 mb-2">
                    <Price
                      amount={Number(balanceSheet.liabilities?.total || 0)}
                    />
                  </p>
                  <div className="space-y-1 text-xs text-amber-700">
                    <div className="flex justify-between">
                      <span>{t("Payables")}:</span>
                      <span>
                        <Price
                          amount={Number(
                            balanceSheet.liabilities?.currentLiabilities
                              ?.accountsPayable || 0,
                          )}
                        />
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t("Pending POs")}:</span>
                      <span>
                        <Price
                          amount={Number(
                            balanceSheet.liabilities?.currentLiabilities
                              ?.pendingPurchaseOrders || 0,
                          )}
                        />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-emerald-900">
                      {t("Total Equity")}
                    </h4>
                    <Wallet className="w-4 h-4 text-emerald-600" />
                  </div>
                  <p className="text-xl font-bold text-emerald-900 mb-2">
                    <Price amount={Number(balanceSheet.equity?.total || 0)} />
                  </p>
                  <div className="flex items-center gap-2 text-xs">
                    {balanceSheet.balanceCheck?.balanced ? (
                      <>
                        <CheckCircle className="w-3 h-3 text-emerald-600" />
                        <span className="text-emerald-700">
                          {t("Balance Sheet Balanced")}
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-3 h-3 text-red-600" />
                        <span className="text-red-700">
                          {t("Balance Sheet Not Balanced")}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Revenue Analytics */}
        {revenueData && (
          <div
            className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 mb-8"
            style={{ animation: "fadeIn 0.5s ease-out 0.45s backwards" }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                  {t("Revenue Analytics")}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t("Detailed revenue breakdown")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-400">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {t("Total Revenue")}
                </h3>
                <p className="text-3xl font-bold text-blue-900">
                  <Price amount={Number(revenueData.totalRevenue)} />
                </p>
                <div className="flex items-center gap-2 mt-2 text-sm">
                  {revenueData.growth >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 text-green-600" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-600" />
                  )}
                  <span
                    className={
                      Number(revenueData.growth) >= 0
                        ? "text-green-700"
                        : "text-red-700"
                    }
                  >
                    {Math.abs(Number(revenueData.growth || 0)).toFixed(1)}%{" "}
                    {t("growth")}
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border border-purple-200">
                <h3 className="text-sm font-semibold text-purple-900 mb-2">
                  {t("Total Orders")}
                </h3>
                <p className="text-3xl font-bold text-purple-900">
                  {formatNumber(Number(revenueData.orderCount || 0))}
                </p>
                <p className="text-sm text-purple-700 mt-2">
                  {t("Monthly")}:{" "}
                  {formatNumber(Number(revenueData.monthly || 0))}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200">
                <h3 className="text-sm font-semibold text-green-900 mb-2">
                  {t("Avg Order Value")}
                </h3>
                <p className="text-3xl font-bold text-green-900">
                  <Price amount={Number(revenueData.avgOrderValue)} />
                </p>
              </div>
            </div>

            {/* Revenue by Method */}
            {revenueData.byMethod && revenueData.byMethod.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  {t("Revenue by Payment Method")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {revenueData.byMethod.map((method, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-[#0d223f] hover:shadow-md transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">
                          {method.method?.charAt(0)?.toUpperCase() +
                            method.method?.slice(1)}
                        </span>
                        <CreditCard className="w-4 h-4 text-gray-500" />
                      </div>
                      <p className="text-xl font-bold text-gray-900">
                        <Price amount={Number(method.total)} />
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {method.count} {t("transactions")}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Revenue by Category */}
            {revenueData.byCategory && revenueData.byCategory.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  {t("Revenue by Category")}
                </h3>
                <div className="space-y-3">
                  {revenueData.byCategory.map((category, index) => {
                    const percentage =
                      (category.amount / revenueData.totalRevenue) * 100;
                    return (
                      <div key={index} className="group">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-700">
                            {category.category}
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600">
                              {Number(percentage).toFixed(1)}%
                            </span>
                            <span className="text-sm font-bold text-gray-900">
                              <Price amount={Number(category.amount)} />
                            </span>
                          </div>
                        </div>
                        <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                          <div
                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg transition-all duration-700 group-hover:from-blue-600 group-hover:to-indigo-700"
                            style={{ width: `${percentage}%` }}
                          >
                            <div className="absolute inset-0 animate-shimmer"></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sales Chart */}
        {salesChartData.length > 0 && (
          <div
            className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 mb-8"
            style={{ animation: "fadeIn 0.5s ease-out 0.5s backwards" }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                  {t("Sales Overview")}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t("Last 7 days performance")}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {salesChartData.map((data, index) => {
                const maxSales = getMaxSales();
                const percentage =
                  maxSales > 0 ? (data.sales / maxSales) * 100 : 0;
                return (
                  <div
                    key={index}
                    className="group"
                    style={{
                      animation: `slideRight 0.5s ease-out ${
                        0.5 + index * 0.1
                      }s backwards`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700 min-w-[80px]">
                        {data.date}
                      </span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">
                          {data.orders} {t("orders")}
                        </span>
                        <span className="font-bold text-gray-900">
                          <Price amount={Number(data.sales)} />
                        </span>
                      </div>
                    </div>
                    <div className="relative h-10 bg-gray-100 rounded-lg overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg transition-all duration-700 group-hover:from-blue-600 group-hover:to-purple-700"
                        style={{ width: `${percentage}%` }}
                      >
                        <div className="absolute inset-0 animate-shimmer"></div>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-700 drop-shadow-sm">
                          {Number(percentage).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Inventory Summary */}
        {inventoryReport && (
          <div
            className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 mb-8"
            style={{ animation: "fadeIn 0.5s ease-out 0.55s backwards" }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Package2 className="w-6 h-6 text-orange-600" />
                  {t("Inventory Summary")}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t("Current stock overview")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  {t("Total Products")}
                </h3>
                <p className="text-2xl font-bold text-gray-900">
                  {inventoryReport.summary.totalProducts}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200">
                <h3 className="text-sm font-semibold text-green-900 mb-2">
                  {t("In Stock")}
                </h3>
                <p className="text-2xl font-bold text-green-900">
                  {inventoryReport.summary.inStock}
                </p>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-5 border border-yellow-200">
                <h3 className="text-sm font-semibold text-yellow-900 mb-2">
                  {t("Low Stock")}
                </h3>
                <p className="text-2xl font-bold text-yellow-900">
                  {inventoryReport.summary.lowStock}
                </p>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-5 border border-red-200">
                <h3 className="text-sm font-semibold text-red-900 mb-2">
                  {t("Out of Stock")}
                </h3>
                <p className="text-2xl font-bold text-red-900">
                  {inventoryReport.summary.outOfStock}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Top Products & Reports */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Top Products */}
          {topProducts.length > 0 && (
            <div
              className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100"
              style={{ animation: "slideRight 0.5s ease-out 0.6s backwards" }}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <Star className="w-6 h-6 text-yellow-500" />
                    {t("Top Products")}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {t("Best selling items")}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {topProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-all border border-gray-100 hover:border-gray-200 hover:shadow-md group"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden shadow-sm">
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Box className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-bold text-gray-900 truncate">
                          {product.name}
                        </h3>
                        <span className="flex-shrink-0 bg-blue-100 text-blue-700 px-2 py-1 rounded-lg text-xs font-bold">
                          #{index + 1}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        {product.category}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">
                            {product.sales} {t("sold")}
                          </span>
                          <span className="font-bold text-green-600">
                            <Price amount={Number(product.revenue)} />
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span className="text-sm font-bold text-gray-700">
                            {product.rating}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reports */}
          {reports.length > 0 && (
            <div
              className="bg-white rounded-2xl shadow-lg sm:shadow-xl p-5 sm:p-6 border border-gray-100"
              style={{ animation: "slideLeft 0.5s ease-out 0.6s backwards" }}
            >
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-indigo-100">
                      <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                    </div>
                    {t("Available Reports")}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    {t("Generate and schedule reports")}
                  </p>
                </div>
              </div>

              {/* Reports */}
              <div className="space-y-3">
                {reports.map((report) => {
                  const isGenerating = generatingReportType === report.type;

                  return (
                    <div
                      key={report.id}
                      className="group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:bg-gradient-to-r hover:from-indigo-50 hover:to-transparent hover:border-indigo-300 transition-all duration-300 hover:shadow-md"
                    >
                      {/* Left */}
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="p-2.5 rounded-lg bg-indigo-100 group-hover:bg-indigo-200 transition">
                          <FileText className="w-5 h-5 text-indigo-600" />
                        </div>

                        <div className="min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 break-words">
                            {report.name}
                          </h3>

                          <p className="text-xs text-gray-500 mt-0.5">
                            {t("Last generated")} •{" "}
                            <span className="text-gray-600">
                              {formatDate(report.generatedAt)}
                            </span>
                          </p>
                        </div>
                      </div>

                      {/* Right Action */}
                      <div className="flex justify-end sm:justify-start">
                        <Tooltip
                          title={
                            canViewExport
                              ? t("Download Report")
                              : t(
                                  "You don't have permission to view/export Report",
                                )
                          }
                        >
                          <button
                            disabled={!canViewExport || !!generatingReportType}
                            onClick={() => openGenerateReportModal(report.type)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg 
                                      bg-indigo-600 text-white text-xs sm:text-sm font-medium
                                      hover:bg-indigo-700 active:scale-95
                                      transition-all duration-200
                                      disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isGenerating ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                            <span className="hidden sm:inline">
                              {isGenerating ? t("Generating") : t("Download")}
                            </span>
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Recent Orders & Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Orders */}
          {recentOrders.length > 0 && (
            <div
              className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
              style={{ animation: "slideRight 0.5s ease-out 0.7s backwards" }}
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                      <ShoppingBag className="w-6 h-6 text-purple-600" />
                      {t("Recent Orders")}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("Latest customer orders")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        {t("Order")}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        {t("Customer")}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        {t("Amount")}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        {t("Status")}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        {t("Time")}
                      </th>
                      <th className="px-4 py-4"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recentOrders.map((order) => {
                      const statusConfig = getOrderStatusConfig(order.status);
                      const StatusIcon = statusConfig.icon;
                      return (
                        <tr
                          key={order.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="bg-purple-100 rounded-lg p-2 mr-3">
                                <ShoppingBag className="w-4 h-4 text-purple-600" />
                              </div>
                              <div>
                                <div className="text-sm font-bold text-gray-900">
                                  {order.id}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {order.items} {t("items")}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {order.customer}
                            </div>
                            <div className="text-xs text-gray-500">
                              {order.customerEmail}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-gray-900">
                              <Price amount={Number(order.amount)} />
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <CreditCard className="w-3 h-3" />
                              {order.paymentMethod}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${statusConfig.color}`}
                            >
                              <StatusIcon
                                size={14}
                                className={statusConfig.iconColor}
                              />
                              {order.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(order.date)}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <button className="text-blue-600 hover:text-blue-800 hover:bg-[#0d223f] p-2 rounded-lg transition-colors">
                              <Eye size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {recentActivity.length > 0 && (
            <div
              className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100"
              style={{ animation: "slideLeft 0.5s ease-out 0.7s backwards" }}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <Activity className="w-6 h-6 text-green-600" />
                    {t("Activity")}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {t("Recent updates")}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {recentActivity.map((activity) => {
                  const ActivityIcon = activity.icon;
                  return (
                    <div
                      key={activity.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all border border-transparent hover:border-gray-200"
                    >
                      <div
                        className={`${activity.color} rounded-lg p-2 flex-shrink-0 shadow-sm`}
                      >
                        <ActivityIcon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 leading-relaxed">
                          {activity.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <Timer className="w-3 h-3" />
                          {formatDate(activity.time)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Low Stock Alert */}
        {lowStockProducts && lowStockProducts.length > 0 && (
          <div
            className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl shadow-xl p-6 border-2 border-yellow-200"
            style={{ animation: "fadeIn 0.5s ease-out 0.8s backwards" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-yellow-500 rounded-xl p-3 shadow-lg animate-pulse">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {t("Low Stock Alert")}
                </h2>
                <p className="text-sm text-gray-600">
                  {lowStockProducts.length} {t("products need restocking")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lowStockProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-xl p-4 border border-yellow-200 hover:border-yellow-300 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={
                            typeof product.name === "string"
                              ? product.name
                              : product.name?.en ||
                                product.name?.ar ||
                                "Product"
                          }
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <PackageX className="w-7 h-7 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-gray-900 truncate">
                        {typeof product.name === "string"
                          ? product.name
                          : product.name?.en ||
                            product.name?.ar ||
                            "Unknown Product"}
                      </div>
                      <div className="text-xs text-gray-500 mb-2">
                        {t("SKU")}: {product.sku || "N/A"}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-red-500 to-yellow-500 h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${Math.min(((product.stock || 0) / (product.threshold || 1)) * 100, 100)}%`,
                            }}
                          ></div>
                        </div>
                        <span className="text-xs font-bold text-red-600 whitespace-nowrap">
                          {product.stock || 0}/{product.threshold || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {generateReportModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={closeGenerateReportModal}
            style={{ animation: "fadeIn 0.3s" }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
              style={{ animation: "slideDown 0.3s" }}
            >
              <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Download size={22} />
                  {t("Download Report")}
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Report Type")}
                  </label>
                  <select
                    value={generateReportData.reportType}
                    onChange={(e) =>
                      setGenerateReportData((prev) => ({
                        ...prev,
                        reportType: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                  >
                    <option value="">{t("Select a report")}</option>
                    {reports.map((report) => (
                      <option key={report.id} value={report.type}>
                        {report.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Format")}
                  </label>
                  <select
                    value={generateReportData.format}
                    onChange={(e) =>
                      setGenerateReportData((prev) => ({
                        ...prev,
                        format: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                  >
                    <option value="excel">{t("Excel")}</option>
                    <option value="pdf">{t("PDF")}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Start Date")}
                  </label>
                  <input
                    type="date"
                    value={generateReportData.startDate}
                    onChange={(e) =>
                      setGenerateReportData((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("End Date")}
                  </label>
                  <input
                    type="date"
                    value={generateReportData.endDate}
                    min={generateReportData.startDate}
                    onChange={(e) =>
                      setGenerateReportData((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                  />
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">
                      {t(
                        "Choose the report type, date range, and format before downloading.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={closeGenerateReportModal}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    onClick={submitGenerateReport}
                    disabled={
                      !generateReportData.reportType ||
                      !generateReportData.format ||
                      !generateReportData.startDate ||
                      !generateReportData.endDate ||
                      isGeneratingSelectedReport
                    }
                    className="flex-1 px-4 py-3 bg-[#0d223f] text-white rounded-lg hover:bg-[#09182d] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isGeneratingSelectedReport ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        {t("Generating...")}
                      </>
                    ) : (
                      <>
                        <Download size={18} />
                        {t("Download Report")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
